import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import AuthService from '~/api/services/auth'
import Dialog from '~/components/Dialog'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import { TermsLink, TermsLinks, TermsSection } from './style'

interface Props {
  /** Reports whether the form may be submitted: true when there are no terms or they're accepted. */
  onCanSubmitChange?: (canSubmit: boolean) => void
}

/**
 * Terms/privacy acceptance — renders the theme settings files (same source as LayoutAuth)
 * with a required checkbox. While files exist and aren't accepted it reports canSubmit = false.
 */
const TermsCheckbox: React.FC<Props> = ({ onCanSubmitChange }) => {
  const { t } = useTranslation()

  const themeData = useContext(ThemeContext)
  const localTheme = localStorage.getItem('theme')
  const settingsData = themeData && JSON.stringify(themeData) !== localTheme ? JSON.parse(localTheme) : themeData

  const [settingsDataFiles, setSettingsDataFiles] = useState(settingsData?.files || [])
  const [accepted, setAccepted] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const [downloadingId, setDownloadingId] = useState<number | false>(false)

  useEffect(() => {
    const localLang = localStorage.getItem('i18nextLng')
    if (settingsData?.files?.length > 0 && settingsData?.files[0]?.code !== localLang) {
      fetchNewStorageData()
    }
  }, [])

  useEffect(() => {
    onCanSubmitChange?.(!settingsDataFiles.length || accepted)
  }, [settingsDataFiles, accepted])

  async function fetchNewStorageData() {
    try {
      const localLang = localStorage.getItem('i18nextLng')
      const responseTermsData = await AuthService.getSettingsData({ languageCode: localLang || 'de' })
      setSettingsDataFiles(responseTermsData.data?.files || [])
    } catch (error) {
      // don't display error
    }
  }

  async function handleDownloadFile(fileName, fileType, id = null) {
    setDownloadingId(id)
    try {
      if (fileType === 'file') {
        const response = await AuthService.getTermFile({ fileName })
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type)
        setFileSource(URL.createObjectURL(blob))
      } else {
        setFileSource(fileName)
      }
    } catch (error) {
      handleErrors(error)
    }
    setDownloadingId(false)
  }

  function renderFileType(item) {
    return <TermsLink
      key={item.id}
      onClick={() => handleDownloadFile(item.filename, item.fileType, item.id)}
    >
      {downloadingId === item.id ? `${item.name}…` : item.name}
    </TermsLink>
  }

  function renderItem(item) {
    return <TermsLink key={item.id} href={item.link} target='_blank' rel='noreferrer'>
      {item.name}
    </TermsLink>
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  if (!settingsDataFiles.length) return null

  return <>
    <TermsSection>
      <Checkbox
        checked={accepted}
        onChange={(e, data) => setAccepted(Boolean(data.checked))}
        label={t('I have read and accept the following:')}
      />
      <TermsLinks>
        {settingsDataFiles.map((item) => item.fileType === 'file' ? renderFileType(item) : renderItem(item))}
      </TermsLinks>
    </TermsSection>
    {fileSource && renderModalPreviewFile()}
  </>
}

export default TermsCheckbox
